import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Loan, RetentionOutcome } from '../types';
import { AlertTriangle, RefreshCw, ArrowLeft, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface RetentionActionModalProps {
  loan: Loan | null;
  isOpen: boolean;
  onClose: () => void;
  onAction: (loanId: string, outcome: RetentionOutcome, notes: string) => void;
}
type Step = 'select' | 'confirm';
export function RetentionActionModal({
  loan,
  isOpen,
  onClose,
  onAction
}: RetentionActionModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [selectedOutcome, setSelectedOutcome] = useState<RetentionOutcome>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedOutcome(null);
      setNotes('');
      setIsSubmitting(false);
    }
  }, [isOpen]);
  if (!loan) return null;
  const handleOutcomeSelect = (outcome: RetentionOutcome) => {
    setSelectedOutcome(outcome);
    setStep('confirm');
  };
  const handleConfirm = async () => {
    setIsSubmitting(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    onAction(loan.id, selectedOutcome, notes);
    setIsSubmitting(false);
    onClose();
  };
  const getOutcomeColor = (outcome: RetentionOutcome) => {
    switch (outcome) {
      case 'Revived - New File':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'Revived - Previous Stage':
        return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={step === 'select' ? `Manage Loan #${loan.loanNumber}` : 'Confirm Action'} description={step === 'select' ? 'Select an action to resolve this retention case.' : 'Please review and confirm your decision.'} maxWidth="lg" data-id="element-53">
      <div className="space-y-6" data-id="element-54">
        {/* Loan Context Header */}
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 grid grid-cols-2 gap-4 text-sm" data-id="element-55">
          <div data-id="element-56">
            <span className="text-slate-500 block text-xs uppercase tracking-wider font-medium mb-1" data-id="element-57">
              Borrower
            </span>
            <span className="font-semibold text-black" data-id="element-58">
              {loan.borrowerName}
            </span>
            <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500" data-id="element-59">
              <Phone className="h-3 w-3" data-id="element-60" /> {loan.borrowerPhone}
            </div>
          </div>
          <div data-id="element-61">
            <span className="text-slate-500 block text-xs uppercase tracking-wider font-medium mb-1" data-id="element-62">
              Loan Officer
            </span>
            <span className="font-semibold text-black" data-id="element-63">{loan.loanOfficer}</span>
          </div>
          <div className="col-span-2" data-id="element-64">
            <span className="text-slate-500 block text-xs uppercase tracking-wider font-medium mb-1" data-id="element-65">
              Retention Reason
            </span>
            <span className="font-medium text-accent-600 bg-accent-50 px-2 py-1 rounded inline-block border border-accent-100" data-id="element-66">
              {loan.retentionComment}
            </span>
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-67">
          {step === 'select' ? <motion.div key="select" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-68">
              <button onClick={() => handleOutcomeSelect('Revived - New File')} className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group text-center h-48" data-id="element-69">
                <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-70">
                  <RefreshCw className="h-6 w-6" data-id="element-71" />
                </div>
                <h3 className="font-semibold text-black mb-2" data-id="element-72">
                  Revive - New File
                </h3>
                <p className="text-xs text-slate-500" data-id="element-73">
                  Restart process with a new loan number.
                </p>
              </button>

              <button onClick={() => handleOutcomeSelect('Revived - Previous Stage')} className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-slate-100 hover:border-emerald-200 hover:bg-emerald-50 transition-all group text-center h-48" data-id="element-74">
                <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-75">
                  <ArrowLeft className="h-6 w-6" data-id="element-76" />
                </div>
                <h3 className="font-semibold text-black mb-2" data-id="element-77">
                  Revive - Previous
                </h3>
                <p className="text-xs text-slate-500" data-id="element-78">
                  Return file to the previous processing stage.
                </p>
              </button>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="space-y-6" data-id="element-79">
              <div className={`p-4 rounded-lg border flex items-center gap-3 ${getOutcomeColor(selectedOutcome)}`} data-id="element-80">
                <AlertTriangle className="h-5 w-5 shrink-0" data-id="element-81" />
                <div data-id="element-82">
                  <p className="text-sm font-medium" data-id="element-83">
                    You are about to:{' '}
                    <span className="font-bold" data-id="element-84">{selectedOutcome}</span>
                  </p>
                  <p className="text-xs opacity-90" data-id="element-85">
                    This action will be recorded in the loan history.
                  </p>
                </div>
              </div>

              <div data-id="element-86">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-87">
                  Resolution Notes{' '}
                  <span className="text-slate-400 font-normal" data-id="element-88">(Optional)</span>
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any context about this decision..." className="w-full min-h-[100px] p-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm resize-none" data-id="element-89" />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-90">
                <Button variant="ghost" onClick={() => setStep('select')} disabled={isSubmitting} data-id="element-91">
                  Back
                </Button>
                <Button variant="primary" onClick={handleConfirm} isLoading={isSubmitting} data-id="element-92">
                  Confirm & Resolve
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </Modal>;
}